import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

// Aliased: this module also exports its own (resolved-for-display) MeetingMember.

import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import { getFullNameFromContact } from 'flow/utils/call-utils-TW';
import { selectVideoMembers, selectVideoSenderVideoActive } from 'flow/selectors/video-selectors';
import { setActiveMeetingMemberListActiveStatus, getContactFromNumber } from 'flow/actions/meeting-actions';
import { selectSenderName, selectSenderAudioActive, selectActiveMeetingMembers, selectActiveMeetingChatSessionId, selectActiveMeetingChatRoomMemberChatUserKeys, selectConferenceMeetingCallingMembers, deriveMeetingMemberGuestInfo } from 'flow/selectors/meeting-selectors';
import { CALLING_DURATION } from 'flow/utils/meeting-constants';

/** A participant currently in the meeting, resolved for display. */

/** A colleague being rung in (outgoing invite) that still warrants a row. */

/**
 * Resolves the active meeting's participants and outgoing-invite ("calling…")
 * rows for the participants panel. Owns the contact/extension bookkeeping that
 * decides which calling rows are still worth showing (an invite is dropped once
 * the invitee is already in the meeting, or once the ring is stale — they left
 * less than CALLING_DURATION ago; see the callingMembers memo below).
 *
 * NOTE: this derivation logic was extracted from the legacy MeetingMembersList,
 * then tightened (real types, memoised outputs, complete dependency arrays) with
 * behaviour preserved. Two legacy quirks remain: the resolved `contact` is `any`
 * because getContactFromNumber is an untyped JS action, and a couple of memos
 * aren't pure — meetingMemberContacts dispatches getContactFromNumber and
 * meetingMemberExtensions reads Date.now() during render.
 */
var useMeetingMembers = function useMeetingMembers() {
  var dispatch = useAppDispatch();
  var ownName = useAppSelector(selectSenderName);
  var meetingMembers = useAppSelector(selectActiveMeetingMembers);
  var videoMembers = useAppSelector(selectVideoMembers);
  // videoMembers has no live entry for ourselves, so read our own state directly.
  var ownAudioActive = useAppSelector(selectSenderAudioActive);
  var ownVideoActive = useAppSelector(selectVideoSenderVideoActive);
  var extensions = useAppSelector(function (state) {
    return state.extensions.get('extensions');
  });
  var callingParticipants = useAppSelector(selectConferenceMeetingCallingMembers);
  var chatSessionId = useAppSelector(selectActiveMeetingChatSessionId);
  var chatRoomMemberKeys = useAppSelector(selectActiveMeetingChatRoomMemberChatUserKeys);
  var getContactFromNumberAction = useCallback(function (number) {
    return dispatch(getContactFromNumber(number));
  }, [dispatch]);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    allMemberExtensions = _useState2[0],
    setAllMemberExtensions = _useState2[1];
  var closeMembersList = function closeMembersList() {
    return dispatch(setActiveMeetingMemberListActiveStatus(false));
  };
  var meetingMemberContacts = useMemo(function () {
    return meetingMembers.map(function (member) {
      var number = member.get('number');
      return {
        member: member,
        contact: number ? getContactFromNumberAction(number) : undefined,
        memberKey: member.get('key')
      };
    }).toArray();
  }, [meetingMembers, getContactFromNumberAction]);
  var meetingMemberExtensions = useMemo(function () {
    return meetingMemberContacts === null || meetingMemberContacts === void 0 ? void 0 : meetingMemberContacts.map(function (_ref) {
      var member = _ref.member,
        contact = _ref.contact;
      var name = member.get('displayName');
      var videoParticipant = videoMembers.get(name);
      var isCallerParticipant = !videoParticipant && name !== ownName;
      var extension = extensions === null || extensions === void 0 ? void 0 : extensions.find(function (ext) {
        return (contact === null || contact === void 0 ? void 0 : contact.get('key')) === ext.getIn(['contact', 'key']);
      });
      return {
        extension: extension,
        leftTime: isCallerParticipant ? Date.now() : null,
        member: member
      };
    });
  }, [extensions, videoMembers, ownName, meetingMemberContacts]);
  var callingExtensions = useMemo(function () {
    // Defined inside the memo so it isn't a phantom dependency.
    var createCallingExtension = function createCallingExtension(_ref2) {
      var _meetingMemberExtensi;
      var extensionKey = _ref2.extensionKey,
        callingEndTime = _ref2.callingEndTime;
      var extension = extensions === null || extensions === void 0 ? void 0 : extensions.find(function (ext) {
        return extensionKey === ext.get('key');
      });
      var member = (_meetingMemberExtensi = meetingMemberExtensions.find(function (meetingMember) {
        var _meetingMember$extens;
        return (meetingMember === null || meetingMember === void 0 || (_meetingMember$extens = meetingMember.extension) === null || _meetingMember$extens === void 0 ? void 0 : _meetingMember$extens.get('key')) === extensionKey;
      })) === null || _meetingMemberExtensi === void 0 ? void 0 : _meetingMemberExtensi.member;
      return {
        extension: extension,
        callingEndTime: callingEndTime,
        member: member
      };
    };
    return (callingParticipants === null || callingParticipants === void 0 ? void 0 : callingParticipants.map(createCallingExtension).toArray()) || [];
  }, [callingParticipants, extensions, meetingMemberExtensions]);

  // Accumulate member extensions across renders: keep prior entries that
  // aren't currently present (so a member who just left is still available
  // for the short-call check), then append the current ones. A Set membership
  // test replaces a nested find — keeping the callback nesting shallow.
  useEffect(function () {
    var currentExtensionKeys = new Set(meetingMemberExtensions.map(function (memberExtension) {
      var _memberExtension$exte;
      return memberExtension === null || memberExtension === void 0 || (_memberExtension$exte = memberExtension.extension) === null || _memberExtension$exte === void 0 ? void 0 : _memberExtension$exte.get('key');
    }));
    setAllMemberExtensions(function (prev) {
      return [].concat(_toConsumableArray(prev.filter(function (participant) {
        var _participant$extensio;
        return !currentExtensionKeys.has(participant === null || participant === void 0 || (_participant$extensio = participant.extension) === null || _participant$extensio === void 0 ? void 0 : _participant$extensio.get('key'));
      })), _toConsumableArray(meetingMemberExtensions));
    });
  }, [meetingMemberExtensions]);

  // Memoised so the arrays (and the member objects inside) keep stable
  // references across re-renders that don't change the underlying data — the
  // panel re-renders whenever its ancestor MeetingVideoCanvas does (resize,
  // speaking detection, …), and the React.memo'd rows skip those re-renders
  // only while these references hold.
  var members = useMemo(function () {
    return meetingMembers.sortBy(function (participant) {
      return participant.get('joinTime');
    }).toArray().map(function (member) {
      var name = member.get('displayName');
      var memberKey = member.get('key');
      var videoParticipant = videoMembers.get(name);
      var memberIsCaller = !videoParticipant && name !== ownName;
      var memberIsSelf = name === ownName;
      var _deriveMeetingMemberG = deriveMeetingMemberGuestInfo(member.get('identity'), chatSessionId, chatRoomMemberKeys),
        isGuestCandidate = _deriveMeetingMemberG.isGuest,
        isInternal = _deriveMeetingMemberG.isInternal,
        chatUserKey = _deriveMeetingMemberG.chatUserKey;
      var isGuest = isGuestCandidate && !memberIsSelf;
      return {
        memberKey: memberKey,
        name: name,
        displayName: sliceUUIDFromUserName(name),
        phoneNumber: member.get('number'),
        callerId: member.get('callerId'),
        memberIsSelf: memberIsSelf,
        memberIsCaller: memberIsCaller,
        isGuest: isGuest,
        isInternal: isInternal,
        chatUserKey: chatUserKey,
        videoActive: memberIsSelf ? ownVideoActive : !!(videoParticipant !== null && videoParticipant !== void 0 && videoParticipant.get('videoActive')),
        audioActive: memberIsSelf ? ownAudioActive : !!(videoParticipant !== null && videoParticipant !== void 0 && videoParticipant.get('audioActive'))
      };
    });
  }, [meetingMembers, videoMembers, ownName, ownAudioActive, ownVideoActive, chatSessionId, chatRoomMemberKeys]);

  // The two row-suppression checks are inlined (rather than separate helpers)
  // so the memo's dependency array honestly lists everything it reads:
  // callingExtensions, meetingMemberContacts (already-in-meeting) and
  // allMemberExtensions (recently-left / short call).
  var callingMembers = useMemo(function () {
    return callingExtensions.map(function (_ref3) {
      var extension = _ref3.extension,
        callingEndTime = _ref3.callingEndTime;
      var contact = extension === null || extension === void 0 ? void 0 : extension.get('contact');
      var name = getFullNameFromContact(contact);

      // Drop the row once the invitee is already in the
      // meeting — matched by contact key, else display name.
      var alreadyInMeeting = meetingMemberContacts.find(function (meetingContact) {
        var _meetingContact$conta;
        if (meetingContact !== null && meetingContact !== void 0 && (_meetingContact$conta = meetingContact.contact) !== null && _meetingContact$conta !== void 0 && _meetingContact$conta.get('key') && contact !== null && contact !== void 0 && contact.get('key')) {
          return meetingContact.contact.get('key') === contact.get('key');
        }
        if (name) {
          var _meetingContact$membe;
          // Some fragile sorcery this, it seems — a
          // pure copy-paste from the legacy component.
          // Would probably be healthy to look into it.
          var memberDisplayName = sliceUUIDFromUserName((_meetingContact$membe = meetingContact.member) === null || _meetingContact$membe === void 0 ? void 0 : _meetingContact$membe.get('displayName'));
          return memberDisplayName === name;
        }
        return false;
      });

      // …and once the ring is stale: the invitee left under
      // CALLING_DURATION ago (a quick re-invite flicker).
      var memberExtension = allMemberExtensions.find(function (memberExt) {
        var _memberExt$extension;
        return (memberExt === null || memberExt === void 0 || (_memberExt$extension = memberExt.extension) === null || _memberExt$extension === void 0 ? void 0 : _memberExt$extension.get('key')) === (extension === null || extension === void 0 ? void 0 : extension.get('key'));
      });
      var callIsShort = callingEndTime - ((memberExtension === null || memberExtension === void 0 ? void 0 : memberExtension.leftTime) || 0) < CALLING_DURATION;
      var callShouldRender = !alreadyInMeeting && !callIsShort;
      return callShouldRender ? {
        key: contact === null || contact === void 0 ? void 0 : contact.get('key'),
        name: name,
        contact: contact
      } : null;
    }).filter(Boolean);
  }, [callingExtensions, meetingMemberContacts, allMemberExtensions]);

  // Single pass shared by every consumer that needs the room/guest split
  // (previously duplicated as members.filter(...) in both
  // MeetingParticipantsList and MeetingRoomMembersView).
  var _useMemo = useMemo(function () {
      var room = [];
      var guests = [];
      var _iterator = _createForOfIteratorHelper(members),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var member = _step.value;
          (member.isGuest ? guests : room).push(member);
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      return {
        roomMembers: room,
        guestMembers: guests
      };
    }, [members]),
    roomMembers = _useMemo.roomMembers,
    guestMembers = _useMemo.guestMembers;
  return {
    members: members,
    roomMembers: roomMembers,
    guestMembers: guestMembers,
    callingMembers: callingMembers,
    count: meetingMembers.size,
    closeMembersList: closeMembersList
  };
};
export default useMeetingMembers;